import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { CaasClient, CaasClientCreateRequest, CaasClientUpdateRequest } from '../../../../types';
import { createClient, updateClient } from '../../../../services/api/endpoints/caas';
import { useAuth } from '../../../../contexts/AuthContext';

interface ClientFormModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  client?: CaasClient | null; // If provided, edit mode; otherwise create mode
}

const ClientFormModal: React.FC<ClientFormModalProps> = ({
  open,
  onClose,
  onSuccess,
  client,
}) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    hubspot_subscription_id: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const isEditMode = !!client;

  // Initialize form data when modal opens or client changes
  useEffect(() => {
    if (open) {
      if (client) {
        setFormData({
          name: client.name,
          address: client.address,
          hubspot_subscription_id: client.hubspot_subscription_id || '',
        });
      } else {
        setFormData({
          name: '',
          address: '',
          hubspot_subscription_id: '',
        });
      }
      setErrors({});
      setApiError(null);
    }
  }, [open, client]);

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
    // Clear error for this field
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: '',
      });
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.length > 255) {
      newErrors.name = 'Name must be 255 characters or less';
    }

    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    } else if (formData.address.length > 500) {
      newErrors.address = 'Address must be 500 characters or less';
    }

    if (formData.hubspot_subscription_id && formData.hubspot_subscription_id.length > 255) {
      newErrors.hubspot_subscription_id = 'HubSpot Subscription ID must be 255 characters or less';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      return;
    }

    if (!user?.token) {
      setApiError('No authentication token available');
      return;
    }

    try {
      setIsSaving(true);
      setApiError(null);

      const payload: CaasClientCreateRequest | CaasClientUpdateRequest = {
        name: formData.name.trim(),
        address: formData.address.trim(),
        hubspot_subscription_id: formData.hubspot_subscription_id.trim() || undefined,
      };

      if (isEditMode && client) {
        await updateClient(user.token, client.id, payload);
      } else {
        await createClient(user.token, payload as CaasClientCreateRequest);
      }

      onSuccess();
    } catch (err: any) {
      console.error('Error saving client:', err);
      setApiError(err.message || 'Failed to save client');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="client-form-modal-title"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '95%', sm: '90%', md: '600px' },
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'grey.50',
          }}
        >
          <Typography id="client-form-modal-title" variant="h6" component="h2">
            {isEditMode ? 'Edit Client' : 'Create Client'}
          </Typography>
          <IconButton onClick={onClose} size="small" disabled={isSaving}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3 }}>
          {apiError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {apiError}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Name"
            value={formData.name}
            onChange={handleChange('name')}
            error={!!errors.name}
            helperText={errors.name || 'Required'}
            disabled={isSaving}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Address"
            value={formData.address}
            onChange={handleChange('address')}
            error={!!errors.address}
            helperText={errors.address || 'Required'}
            disabled={isSaving}
            multiline
            rows={2}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="HubSpot Subscription ID"
            value={formData.hubspot_subscription_id}
            onChange={handleChange('hubspot_subscription_id')}
            error={!!errors.hubspot_subscription_id}
            helperText={errors.hubspot_subscription_id || 'Optional'}
            disabled={isSaving}
            sx={{ mb: 3 }}
          />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button onClick={onClose} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isSaving}
            >
              {isSaving ? <CircularProgress size={24} /> : isEditMode ? 'Save' : 'Create'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default ClientFormModal;
